import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router';

import { useCustomToast } from './useCustomToast';
import {
  AADSTS_CODE_PATTERN,
  EXPECTED_OAUTH_REFUSALS,
  isOAuthLandingRoute,
  KNOWN_OAUTH_PROVIDERS,
  normalizeLandingPathname,
  OAUTH_ERROR_PARAMS,
  OAUTH_PROVIDER_BY_ROUTE,
  OAUTH_PROVIDER_PARAMS,
  OAUTH_REFUSAL_CODE_DISALLOWED,
  OAUTH_REFUSAL_CODE_MAX_LENGTH,
  OAUTH_REFUSAL_DEDUPE_WINDOW_MS,
  OAUTH_REFUSAL_I18N_KEYS,
  OAUTH_REFUSAL_MESSAGE_KEYS,
  OAUTH_STATE_PARAM,
  OAUTH_SUCCESS_PARAM,
  UNKNOWN_OAUTH_PROVIDER,
} from '../utils/constants/oauthErrorConstants';
import { buildPathWithoutOAuthParams } from '../utils/constants/oauthQueryParams';
import { clearPostOAuthFlags } from '../utils/postOAuthReturn';
import { useSentryLogger } from '../utils/sentry';

const OAUTH_ERROR_NOTIFIER = 'OAuthErrorNotifier';

// Survives StrictMode's double-invoke and a remount that races the URL cleanup.
// Time-windowed rather than permanent: a refusal code is not single-use, so a user
// retrying the connection must be told again.
const reportedRefusals = new Map<string, number>();

const hasAlreadyReported = (key: string): boolean => {
  const now = Date.now();

  for (const [seenKey, seenAt] of reportedRefusals) {
    if (now - seenAt > OAUTH_REFUSAL_DEDUPE_WINDOW_MS) {
      reportedRefusals.delete(seenKey);
    }
  }

  if (reportedRefusals.has(key)) {
    return true;
  }

  reportedRefusals.set(key, now);
  return false;
};

/**
 * The provider's own refusal code. Microsoft's `error` is a coarse OAuth category and
 * the cause lives in the description — `AADSTS65004` arrives as `access_denied`, which
 * is indistinguishable from a real cancellation without it.
 */
export const resolveRefusalCode = (
  error: string,
  errorDescription: string | null,
): string => {
  const aadstsCode = errorDescription?.match(AADSTS_CODE_PATTERN)?.[0];
  return aadstsCode ?? error;
};

export const sanitizeRefusalCode = (code: string): string =>
  code
    .replace(OAUTH_REFUSAL_CODE_DISALLOWED, '')
    .slice(0, OAUTH_REFUSAL_CODE_MAX_LENGTH);

const resolveProvider = (queryParams: URLSearchParams, pathname: string) => {
  const fromQuery = OAUTH_PROVIDER_PARAMS.map((param) =>
    queryParams.get(param),
  ).find(Boolean);

  const [, , subroute] = pathname.split('/');
  const candidate =
    fromQuery ?? OAUTH_PROVIDER_BY_ROUTE.get(pathname) ?? subroute;

  // Both sources are attacker-supplied — the query string directly, the path via a
  // crafted /integration/<anything> link. Anything outside the platforms we connect is
  // someone else's text, and each distinct value would open its own Sentry issue.
  return candidate && KNOWN_OAUTH_PROVIDERS.has(candidate)
    ? candidate
    : UNKNOWN_OAUTH_PROVIDER;
};

export const useOAuthProviderError = (): void => {
  const { t } = useTranslation();
  const { errorMessage } = useCustomToast();
  const { logException, logInfo } = useSentryLogger();
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    const queryParams = new URLSearchParams(location.search);
    const error = queryParams.get(OAUTH_ERROR_PARAMS.ERROR);

    // A redirect carries a grant or a refusal, never both. The grant belongs to the
    // handlers that already own it, and this path must not touch their URL.
    if (!error || queryParams.get(OAUTH_SUCCESS_PARAM)) {
      return;
    }

    // One normalization for the whole effect. Reading the raw pathname after the
    // landing check normalized it made `/inbox/` pass the guard and then resolve to
    // `provider=unknown`, on the one provider whose route is the only signal.
    const pathname = normalizeLandingPathname(location.pathname);

    if (!isOAuthLandingRoute(pathname)) {
      return;
    }

    clearPostOAuthFlags();

    const errorDescription = queryParams.get(
      OAUTH_ERROR_PARAMS.ERROR_DESCRIPTION,
    );
    // Attacker-controllable: the whole URL is, and this value lands verbatim inside a
    // first-party error toast. No XSS (React renders it as a text child) but an
    // unbounded one would carry arbitrary copy under our own branding.
    const code = sanitizeRefusalCode(
      resolveRefusalCode(error, errorDescription),
    );
    const state = queryParams.get(OAUTH_STATE_PARAM) ?? '';

    if (hasAlreadyReported(`${pathname}|${code}|${state}`)) {
      return;
    }

    const provider = resolveProvider(queryParams, pathname);
    const messageKey = OAUTH_REFUSAL_MESSAGE_KEYS.get(code);

    errorMessage(
      messageKey ? t(messageKey) : t(OAUTH_REFUSAL_I18N_KEYS.GENERIC, { code }),
      t(OAUTH_REFUSAL_I18N_KEYS.TITLE),
    );

    // The code and the `error` value only. `error_description` is provider-authored and
    // unbounded — AADSTS50020 embeds the user's own account address — and telemetry
    // takes the opaque Clerk id and nothing else identifying.
    //
    // A plain cancellation is the most common refusal there is and is not a fault, so it
    // goes in at info level. Raising it as an exception would let ordinary user
    // behaviour set the project's error rate and drown the codes worth paging on.
    const summary = `OAuth refusal: ${code} provider=${provider}`;

    if (EXPECTED_OAUTH_REFUSALS.has(code)) {
      logInfo(summary, pathname, OAUTH_ERROR_NOTIFIER);
    } else {
      logException(new Error(summary), summary, pathname, OAUTH_ERROR_NOTIFIER);
    }

    navigate(buildPathWithoutOAuthParams(pathname, queryParams), {
      replace: true,
    });
    // Omits t/errorMessage/logException/navigate on purpose: useCustomToast hands back
    // fresh identities every render, so listing them re-fires the toast in a loop. The
    // sibling useOAuthCalendarConnection does list them — do not harmonize the two.
  }, [location.pathname, location.search]);
};
